import React from "react";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { Checkbox } from "@progress/kendo-react-inputs";
import { CgDanger } from "react-icons/cg";
import IconComponent from "../common/IconComponent";

interface BaseOption {
  value: string;
  label: string;
  [key: string]: any;
}

interface CardConfig {
  layout: "service" | "document";
  showBadge?: boolean;
  badgePosition?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  showPrice?: boolean;
  pricePrefix?: string;
  showSubtitle?: boolean;
  showDescription?: boolean;
  showCode?: boolean;
  codePosition?: "left" | "right" | "top";
  containerClass?: string;
  cardClass?: string;
}

interface CardCheckboxGroupProps extends FieldRenderProps {
  options: BaseOption[];
  cardConfig: CardConfig;
  customRenderer?: (
    option: BaseOption,
    isSelected: boolean,
    isDisabled: boolean
  ) => React.ReactNode;
  disabledOptions?: string[];
  isSelectAll?: boolean;
}

const CardCheckboxGroup = (fieldRenderProps: CardCheckboxGroupProps) => {
  const {
    validationMessage,
    touched,
    id,
    label,
    valid,
    disabled,
    hint,
    wrapperClassName,
    wrapperStyle,
    astrike = false,
    options = [],
    value = [],
    onChange,
    cardConfig,
    customRenderer,
    disabledOptions = [],
    isSelectAll = false,
    ...others
  } = fieldRenderProps;

  const editorRef = React.useRef<any>(null);

  const showValidationMessage: string | false | null =
    touched && validationMessage;
  const showHint: boolean = !showValidationMessage && hint;
  const hintId: string = showHint ? `${id}_hint` : "";
  const errorId: string = showValidationMessage ? `${id}_error` : "";
  const labelId: string = label ? `${id}_label` : "";
  const selectedValues = Array.isArray(value) ? value : [];

  const handleCardClick = (optionValue: string) => {
    if (!disabled && onChange) {
      const updatedValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((val: any) => val !== optionValue)
        : [...selectedValues, optionValue];

      onChange({ value: updatedValues });
    }
  };

  const renderServiceCard = (
    option: BaseOption,
    isSelected: boolean,
    isDisabled: boolean
  ) => (
    <div className="d-flex align-items-center gap-2">
      <Checkbox
        tabIndex={-1}
        className="me-2"
        checked={isSelected}
        disabled={isDisabled}
        onChange={() => handleCardClick(option.value)}
        id={`checkbox_${option.value}`}
      />
      <div>
        <span className="fw-bold fs-6">{option?.label}</span>
        <p className="text-sm m-0">{option?.ServiceDescription}</p>
        <span className="fw-600" style={{ color: "#007bff" }}>
          {option?.Price
            ? new Intl.NumberFormat("en-IN", {
                style: "currency",
                currency: "INR",
              }).format(option?.Price)
            : 0}
        </span>
      </div>
    </div>
  );

  const renderDocumentCard = (
    option: BaseOption,
    isSelected: boolean,
    isDisabled: boolean
  ) => (
    <div className="d-flex align-items-center gap-2">
      <Checkbox
        tabIndex={-1}
        className="me-2"
        checked={isSelected}
        disabled={isDisabled}
        onChange={() => handleCardClick(option.value)}
        id={`checkbox_${option.value}`}
      />
      <div>
        <span className="fw-bold fs-6">{option?.label}</span>
      </div>
    </div>
  );

  const getCardRenderer = (
    option: BaseOption,
    isSelected: boolean,
    isDisabled: boolean
  ) => {
    if (customRenderer) {
      return customRenderer(option, isSelected, isDisabled);
    }

    switch (cardConfig.layout) {
      case "service":
        return renderServiceCard(option, isSelected, isDisabled);
      case "document":
        return renderDocumentCard(option, isSelected, isDisabled);
      default:
        return renderServiceCard(option, isSelected, isDisabled);
    }
  };

  const getContainerClass = () => {
    const baseClass = "checkbox-card-container";
    const layoutClass = `${baseClass}-${cardConfig.layout}`;
    const customClass = cardConfig.containerClass || "";
    return `${baseClass} ${layoutClass} ${customClass}`.trim();
  };

  const getCardClass = (isSelected: boolean, isDisabled: boolean) => {
    const baseClass = "checkbox-card";
    const layoutClass = `${baseClass}-${cardConfig.layout}`;
    const selectedClass = isSelected ? "flexible-card-selected" : "";
    const disabledClass = isDisabled ? "flexible-card-disabled" : "";
    const customClass = cardConfig.cardClass || "";
    return `${baseClass} ${layoutClass} ${selectedClass} ${disabledClass} ${customClass}`.trim();
  };

  return (
    <FieldWrapper className={wrapperClassName}>
      {label && (
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
          className="k-form-label flexible-card-label"
        >
          {label} {astrike && <span style={{ color: "red" }}>*</span>}
        </Label>
      )}

      <div className="k-form-field-wrap" style={wrapperStyle}>
        {/* Select All Option */}
        {isSelectAll && options?.length > 0 && (
          <Checkbox
            label="Select All"
            checked={value.length === options.length}
            disabled={disabled}
            onChange={() =>
              onChange({
                value:
                  value.length === options.length
                    ? []
                    : options.map((o) => o.value),
              })
            }
            {...others}
          />
        )}

        {/* Flexible Card Checkboxes */}
        <div className={getContainerClass()}>
          {options?.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            const isDisabled =
              disabled || disabledOptions.includes(option.value);

            return (
              <div
                key={option.value}
                className={getCardClass(isSelected, isDisabled)}
                onClick={() => !isDisabled && handleCardClick(option.value)}
                role="checkbox"
                aria-checked={isSelected}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    !isDisabled && handleCardClick(option.value);
                  }
                }}
              >
                {getCardRenderer(option, isSelected, isDisabled)}
              </div>
            );
          })}
        </div>

        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error className="d-flex justify-content-end" id={errorId}>
            <span
              className="d-flex align-items-center"
              style={{
                background: "#DF4957",
                color: "white",
                padding: "1px 8px 1px 5px",
                borderRadius: 10,
                gap: 5,
              }}
            >
              <IconComponent icon={CgDanger} /> {validationMessage}
            </span>
          </Error>
        )}
      </div>
    </FieldWrapper>
  );
};

export default CardCheckboxGroup;
